"use client";

import * as AccordionPrimitive from "@radix-ui/react-accordion";
import { ChevronDown } from "lucide-react";
import { cn } from "@punchup/utils/cn";

export interface StepAccordionSection {
  id: string;
  step: number;
  title: string;
  description: string;
  content: React.ReactNode;
}

export interface StepAccordionProps {
  sections: StepAccordionSection[];
  value: string;
  onValueChange: (value: string) => void;
  className?: string;
}

export function StepAccordion({
  sections,
  value,
  onValueChange,
  className,
}: StepAccordionProps) {
  return (
    <AccordionPrimitive.Root
      type="single"
      collapsible
      value={value}
      onValueChange={onValueChange}
      className={cn("flex flex-col gap-3", className)}
    >
      {sections.map((section) => {
        const isOpen = value === section.id;
        return (
          <AccordionPrimitive.Item
            key={section.id}
            value={section.id}
            className={cn(
              "rounded-xl border bg-white transition-colors",
              isOpen ? "border-orange-400" : "border-gray-200"
            )}
          >
            <AccordionPrimitive.Header>
              <AccordionPrimitive.Trigger className="flex w-full items-center gap-4 px-5 py-4 text-left outline-none">
                <span
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors",
                    isOpen
                      ? "bg-orange-500 text-white"
                      : "bg-gray-200 text-gray-600"
                  )}
                >
                  {section.step}
                </span>
                <div className="flex-1">
                  <p className="text-base font-semibold text-gray-900">
                    {section.title}
                  </p>
                  <p className="text-sm text-gray-500">{section.description}</p>
                </div>
                <ChevronDown
                  size={18}
                  className={cn(
                    "shrink-0 transition-all duration-200",
                    isOpen ? "rotate-180 text-orange-500" : "text-gray-400"
                  )}
                />
              </AccordionPrimitive.Trigger>
            </AccordionPrimitive.Header>
            <AccordionPrimitive.Content className="data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down overflow-hidden">
              <div className="border-t border-gray-100 px-5 py-5">
                {section.content}
              </div>
            </AccordionPrimitive.Content>
          </AccordionPrimitive.Item>
        );
      })}
    </AccordionPrimitive.Root>
  );
}
